import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input, Field, Button, ToggleButton } from "@fluentui/react-components";
import "./styles.scss";
import { ROUTES } from "#root/routes/constants";
import { showToast } from "#root/components/GlobalToaster";
import { useCreateAccountRequest } from "#root/queries/accountRequests";
import { VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import { AxiosError } from "axios";
import MetaData from "../MetaData";
import { CheckmarkFilled } from "@fluentui/react-icons";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface SignUpForm {
  firstName: string;
  lastName: string;
  organizationName: string;
  email: string;
  phoneNumber: string;
  isNewOrganization: boolean;
}

const SignUpContent: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [signUpForm, setSignUpForm] = useState<SignUpForm>({
    firstName: "",
    lastName: "",
    organizationName: "",
    email: "",
    phoneNumber: "",
    isNewOrganization: false
  });

  const createAccountRequest = useCreateAccountRequest();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!executeRecaptcha) {
      showToast('ReCaptcha not ready yet', 'error', 'top');
      return;
    }

    try {
      const token = await executeRecaptcha();
      await createAccountRequest.mutateAsync({
        accountRequest: signUpForm, 
        isNewOrganization: signUpForm.isNewOrganization,
        recaptchaToken: token
      });
      setIsSubmitted(true);
    } 
    catch (error) {
      const errorMessage = error instanceof AxiosError 
        ? error.response?.data?.Message 
        : undefined;
      
      showToast(
        errorMessage || 'Failed to submit sign up request, please try again later',
        'error',
        'top'
      );
    }
  };

  return (
    <div className="signup-page">
      <MetaData 
        title="Sign Up" 
        description="Sign up for your EMC account"
      />
      <div className="signup-page__left" />
      <div className="signup-page__right">
        <div className="signup-page__form-container">
          <div className="signup-page__form-header">
            <img src="/images/emc-logo.svg" alt="EMC Logo" className="signup-page__logo" />
          </div>
          {isSubmitted ? (
            <div className="confirmation-container">
              <div className="confirmation">
                <CheckmarkFilled className="success-icon" />
                <div className='subtitle'>Thank you for submitting your {signUpForm.isNewOrganization ? 'organization' : 'user account'} request</div>
                <p>You will receive an email with further instructions once your request is approved.</p>
              </div>
            </div>
          ) : (
            <>
              <h1 className="signup-page__title">Sign Up</h1>
              <form className="auth-form" onSubmit={handleSubmit} >
                <div className="signup-toggle">
                  <ToggleButton
                    appearance="transparent"
                    checked={!signUpForm.isNewOrganization}
                    onClick={() => setSignUpForm({ ...signUpForm, isNewOrganization: false })}
                  >
                    Join Existing Organization
                  </ToggleButton>
                  <ToggleButton
                    appearance="transparent"
                    checked={signUpForm.isNewOrganization}
                    onClick={() => setSignUpForm({ ...signUpForm, isNewOrganization: true })}
                  >
                    Sign Up New Organization
                  </ToggleButton>
                </div>
                <div className="field-two-columns">
                  <Field label="First Name" required>
                    <Input value={signUpForm.firstName} onChange={(e) => setSignUpForm({ ...signUpForm, firstName: e.target.value })} />
                  </Field>
                  <Field label="Last Name" required>
                    <Input value={signUpForm.lastName} onChange={(e) => setSignUpForm({ ...signUpForm, lastName: e.target.value })} />
                  </Field>
                </div>
                <Field label="Organization Name" required>
                  <Input value={signUpForm.organizationName} onChange={(e) => setSignUpForm({ ...signUpForm, organizationName: e.target.value })} />
                </Field>
                <Field label="Email Address" required>
                  <Input type="email" value={signUpForm.email} onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })} />
                </Field>
                <Field label="Phone Number">
                  <Input 
                    type="tel" 
                    pattern={VALIDATION_PATTERNS.PHONE} 
                    title={VALIDATION_MESSAGES.PHONE} 
                    value={signUpForm.phoneNumber} 
                    onChange={(e) => setSignUpForm({ ...signUpForm, phoneNumber: e.target.value })} 
                  />
                </Field>

                <div>
                  <Button
                    type="submit"
                    appearance="primary"
                    disabled={createAccountRequest.isPending}
                  >
                    {createAccountRequest.isPending ? 'Signing up...' : 'Sign Up'}
                  </Button>
                </div>

                <div className="signup-text">
                  Already have an account?
                  <Link to={ROUTES.LOGIN}>Login</Link>
                </div>
              </form>
            </>
          )}
        </div>
        <div className="signup-page__footer">
          <p className="signup-page__copyright">
            &copy; {new Date().getFullYear()} EMC
          </p>
        </div>
      </div>
    </div>
  );
};

const SignUp: React.FC = () => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}>
      <SignUpContent />
    </GoogleReCaptchaProvider>
  );
};

export default SignUp;